import { CloudUploadOutlined, FileTextOutlined } from '@ant-design/icons';
import { App, Button, Empty, Tag, Upload } from 'antd';
import { useState } from 'react';
import { api } from '../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel } from '../components';
import { formatDate, timeAgo, useLoad } from '../lib';

/**
 * Release notes (owner, 2026-11-03): what changed in each desktop exe build, newest first. Every build of the desktop
 * app writes release-notes/latest.json (scripts/release-notes.mjs in kivonexclient); Add build notes takes that file.
 */
export function ReleaseNotesPage() {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);
  const { data, error, loading, reload } = useLoad(() => api('/v1/admin/releases/notes').then((body) => body.builds ?? []));

  async function add(file) {
    setBusy(true);
    try {
      const notes = JSON.parse(await file.text());
      await api('/v1/admin/releases/notes', { method: 'POST', body: JSON.stringify(notes) });
      message.success(`Release notes for ${notes.version} (build ${notes.build}) added.`);
      await reload();
    } catch (err) {
      message.error(err instanceof SyntaxError ? 'That file is not release-notes/latest.json.' : err instanceof Error ? err.message : 'The notes could not be added.');
    } finally {
      setBusy(false);
    }
  }

  const builds = data ?? [];
  return <>
    <PageHeader
      title="Release notes"
      description="What changed in each desktop build, newest first. Every exe build writes release-notes/latest.json; add it here."
      actions={<Upload accept=".json,application/json" showUploadList={false} beforeUpload={(file) => { void add(file); return false; }} disabled={busy}>
        <Button type="primary" icon={<CloudUploadOutlined />} loading={busy}>Add build notes</Button>
      </Upload>}
    />
    <ErrorNotice error={error} />
    {loading && !data ? <FormSkeleton sections={2} /> : builds.length === 0 ? <Panel title="No builds yet"><Empty description="Add release-notes/latest.json after the next exe build." /></Panel> : builds.map((build) => (
      <Panel
        key={build.build}
        title={<span className="rn-title"><FileTextOutlined /> {build.version} <Tag variant="filled">build {build.build}</Tag></span>}
        description={`Built ${formatDate(build.builtAt)} (${timeAgo(build.builtAt)}) from ${build.commit || '—'}${build.previousBuild ? ` · since ${build.previousBuild}` : ' · first recorded build'} · ${build.changes.length} change${build.changes.length === 1 ? '' : 's'}`}
      >
        {build.changes.length === 0 ? <p className="rn-empty">No code changes since the previous build.</p> : <ul className="rn-list">
          {build.changes.map((change) => <li key={change.hash}>
            <strong>{change.subject}</strong> <span className="rn-meta">{change.hash} · {formatDate(change.date)}</span>
            {change.details?.length > 0 && <ul>{change.details.map((detail, index) => <li key={index}>{detail}</li>)}</ul>}
          </li>)}
        </ul>}
      </Panel>
    ))}
  </>;
}
